import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ExchangeTransaction,
  TransactionStatusLog,
  Response,
  Channel,
  ChangeLog,
  Pagination,
  DocumentComment,
} from '@ui/src';
import { ProblemTransactionCofProps, OrganizationType } from '../../../types';
import { customersComponentApi } from '../../../../customers/store/services/customers';

export interface ProblemExchangeTransactionState {
  transaction?: ExchangeTransaction & { exchangeCurrencyFixedSum: string };
  transactionId?: string;
  requestInProgress?: boolean;
  customerType?: OrganizationType;
  logs?: TransactionStatusLog[];
  chats?: Channel[];
  senderCompanyOfficials?: ProblemTransactionCofProps[];
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  transactionComments?: DocumentComment[];
  transactionCommentsPagination?: Pagination;
}

const initialState: ProblemExchangeTransactionState = {
  transaction: undefined,
  transactionId: undefined,
};

export const problemExchangeTransactionSlice = createSlice({
  name: 'problemExchangeTransaction',
  initialState,
  reducers: {
    clearTransactionState: (state) => {
      state.transaction = undefined;
      state.transactionId = undefined;
      state.customerType = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.transactionComments = undefined;
      state.transactionCommentsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getTransactionStatementsChangeLogs
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersTransactionLogs
        .matchFulfilled,
      (state, action: PayloadAction<Response<TransactionStatusLog>>) => {
        state.logs = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getProblemTransactionSenderClientsOfficias
        .matchFulfilled,
      (state, action: PayloadAction<Response<ProblemTransactionCofProps>>) => {
        state.senderCompanyOfficials = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder
      .addMatcher(
        customersComponentApi.endpoints.getCustomerExchangeTransaction
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: ExchangeTransaction & { exchangeCurrencyFixedSum: string };
          }>,
        ) => {
          const transaction = action.payload.data;

          state.transaction = {
            ...transaction,
            providersRate: transaction?.exchangeRate,
            exchangeType:
              transaction.exchangeCurrencyFixedId ===
              transaction.exchangeCurrencyFromId
                ? 'SELL'
                : 'BUY',
            baseRate: transaction.exchangeRate,
            sum:
              transaction.exchangeCurrencyFixedId ===
              transaction.exchangeCurrencyFromId
                ? transaction.sum
                : (transaction.exchangeCurrencyFlexibleSum as number),
            calculatedAmount:
              transaction.exchangeCurrencyFixedId ===
              transaction.exchangeCurrencyFromId
                ? transaction.exchangeCurrencyFlexibleSum?.toString()
                : transaction.sum.toString(),
          };
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getProblemTransactionChats
          .matchFulfilled,
        (state, action: PayloadAction<Response<Channel>>) => {
          state.chats = action.payload.data.edges.map((item) => item.node);
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.createProblemTransactionChat
          .matchFulfilled,
        (state, action: PayloadAction<{ data: Channel }>) => {
          const nextChats = ([] as Channel[]).concat(state.chats as Channel[]);

          nextChats.unshift(action.payload.data);

          state.chats = nextChats;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getProblemTransactionComments
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<DocumentComment>> & {
            meta: {
              arg: {
                originalArgs: {
                  cursor?: string;
                };
              };
            };
          },
        ) => {
          let comments;

          if (!action.meta?.arg?.originalArgs?.cursor) {
            comments = action.payload.data.edges?.map((item) => item.node);
          } else {
            comments = ([] as DocumentComment[]).concat(
              state.transactionComments as DocumentComment[],
            );

            action.payload.data.edges?.forEach((item) =>
              comments.push(item.node),
            );
          }

          state.transactionComments = comments;
          state.transactionCommentsPagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.addCommentToProblemTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          let comments: DocumentComment[] = [];

          if (state.transactionComments) {
            comments = ([] as DocumentComment[]).concat(
              state.transactionComments,
            );
          }

          comments.unshift(action.payload.data);

          state.transactionComments = comments;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.editCommentInProblemTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          state.transactionComments = state.transactionComments?.map((item) => {
            const result = { ...item };

            if (result.id === item.id) {
              result.text = action.payload.data.text;
            }

            return result;
          });
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.deleteCommentInProblemTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: DocumentComment }>) => {
          const nextTranscationComments = state?.transactionComments?.filter(
            (item) => item.id !== action.payload.data.id,
          );

          state.transactionComments = nextTranscationComments;
        },
      );
  },
});

export const { clearTransactionState } =
  problemExchangeTransactionSlice.actions;

export default problemExchangeTransactionSlice.reducer;
